/**
 * Push notification registration and permission flow.
 *
 * Actual push SENDING requires a server-side endpoint (Supabase Edge Function).
 * This module handles the client side: permission, service worker, subscription storage.
 */

const DISMISS_KEY = 'bigstar_push_dismissed';

export function isPushSupported() {
  return 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
}

export function isPushDismissed() {
  try {
    const ts = localStorage.getItem(DISMISS_KEY);
    if (!ts) return false;
    // Re-ask after 7 days
    return Date.now() - Number(ts) < 7 * 86400000;
  } catch { return false; }
}

export function dismissPush() {
  try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch {}
}

export function isPushGranted() {
  return isPushSupported() && Notification.permission === 'granted';
}

export async function registerPush() {
  if (!isPushSupported()) return null;

  const permission = await Notification.requestPermission();
  if (permission !== 'granted') return null;

  try {
    const registration = await navigator.serviceWorker.register('/sw.js');

    // VAPID public key from env — if not set, skip subscription
    const vapidKey = process.env.REACT_APP_VAPID_PUBLIC_KEY;
    if (!vapidKey) {
      // Service worker registered (for future push), but no subscription yet
      return { registered: true, subscription: null };
    }

    const subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: vapidKey,
    });

    return { registered: true, subscription: subscription.toJSON() };
  } catch (err) {
    console.warn('Push registration failed:', err);
    return null;
  }
}
